import { defineComponent as _defineComponent } from 'vue'
import { Aggregation, Resolution, ResourceType } from '@/clients/dashboardapi/v2';
import Utils from '@/assets/js/utils/Utils';
import BubbleMenu from '@/components/basics/BubbleMenu.vue';
import WidgetTimeChooser from '@/components/widgets/components/WidgetTimeChooser.vue';
import ConfirmDlg from '@/modules/shared/components/dialogs/generic/ConfirmDlg.vue';
import { WidgetWizard, WidgetWizardTab } from '@/components/widget-wizard';
import DashboardWidgetInfo from '@/components/widgets/components/DashboardWidgetInfo.vue';
import CopyWidgetDlg from '@/modules/ctx-dashboard/components/widgets/CopyWidgetDlg.vue';
import InfoDlg from '@/modules/shared/components/dialogs/generic/InfoDlg.vue';
import { LicenseFeature, Role } from '@/modules/shared/types';
import MissingLicenseDlg from '@/modules/shared/components/dialogs/generic/MissingLicenseDlg.vue';
import WidgetLoader from '@/components/widgets/components/WidgetLoader.vue';

import type { Dashboard, Portfolio, WidgetConfig, WidgetConfigResource } from '@/modules/ctx-dashboard';
import { WidgetType } from '@/modules/ctx-dashboard';
import { defaultSettings, WidgetSettings, WidgetUtils } from '@/components/widgets';
import { Port } from '@/modules/ctx-dashboard/Port';
import ArrayUtils from '@/assets/js/utils/ArrayUtils';
import NoteBox from '@/components/basics/NoteBox.vue';
import RoleGuard from '@/modules/shared/components/util/RoleGuard.vue';
import FileUtils from '@/assets/js/utils/FileUtils';
import { v4 as generateUUID } from 'uuid';
import CreateWidgetTemplateDlg from '@/modules/ctx-dashboard/components/widgets/CreateWidgetTemplateDlg.vue';
import DateUtils from '@/assets/js/utils/DateUtils';
import WidgetStreaming from '@/components/widgets/components/DashboardWidgetStreaming.vue';
import WidgetStreamingFailed from '@/components/widgets/components/DashboardWidgetStreamingFailed.vue';
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { MenuAction } from '@/components/widgets/types/MenuAction';
import { i18n } from '@/plugins/i18n';
import { useRoute } from 'vue-router/composables';
import store from '@/store/store';

type Props = {
    config: WidgetConfig;
    dashboardKey?: string;
    report: boolean;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'DashboardWidget',
  props: {
    config: null,
    dashboardKey: null,
    report: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
    config: WidgetConfig;
    dashboardKey?: string;
    report: boolean;
};



const route = useRoute();

const widgetLoader = ref<any|null>(null);
const widgetTitle = ref<HTMLElement|null>(null);
const widgetInfo = ref<string>('');
const menuActions = ref<MenuAction[]>([]);
// actual copies are made in the created event, as accessing the props right here will cause unintended
// strange behavior
// writable copy of config used for editing
const widget = ref<WidgetConfig>({} as WidgetConfig);
// copy used to make temporary changes that should not appear in the editor
const widgetOverride = ref<WidgetConfig>({} as WidgetConfig);
const wizardTab = ref<WidgetWizardTab|null>(null);
const wizardTabAction = ref<string|null>(null);
const dataIntegrity = ref<number>(100);
const widgetSettings = ref<WidgetSettings>(defaultSettings);
const portfolio = ref<Portfolio|null>(null);
const dashboard = ref<Dashboard|null>(null);

const showMissingDataDlg = ref<boolean>(false);
const showCopyDlg = ref<boolean>(false);
const showMissingLicenseDlg = ref<boolean>(false);
const showDeleteConfirmDlg = ref<boolean>(false);
const showCreateTemplateDlg = ref<boolean>(false);
const showRetryStreamingDlg = ref<boolean>(false);
const showWizardDlg = ref<boolean>(false);
const showDropdown = ref<boolean>(false);
const showInfo = ref<boolean>(false);
const expand = ref<boolean>(false);
const isFavorite = ref<boolean>(false);

const portfolioName = computed<string>(() => portfolio.value?.name || i18n.t('unknown').toString());
const dashboardName = computed<string>(() => dashboard.value?.name || i18n.t('unknown').toString());
const title = computed<string>(() => widget.value.title
    || WidgetUtils.getDefaultWidgetTitle(widget.value)
    || widget.value.generatedTitle
    || i18n.t(`widgets.title.for-preset.${widget.value.preset}`).toString());
const useHeaderBackground = computed<boolean>(() => {
    switch (widget.value.type) {
        case WidgetType.Map: return true;
        case WidgetType.WeatherMap: return true;
        default: return false;
    }
});

async function fetchPortfolio(): Promise<void> {
    portfolio.value = await Port.portfolios.getPortfolio(widget.value.portfolioKey);
}
async function fetchDashboard(): Promise<void> {
    dashboard.value = await Port.dashboards.getDashboard(widget.value.dashboardKey);
}
async function onWidgetConfigChanged(): Promise<void> {
    widget.value = Utils.deepCopy(props.config);
    widgetOverride.value = Utils.deepCopy(widget.value);
    isFavorite.value = await Port.widgets.isFavoriteWidget(widget.value.key);
    applyUrlOverrides();
}
function overrideTimeInterval(interval?: { name: string; from: Date; to: Date; resolution?: Resolution }): void {
    const config = Utils.deepCopy(widgetOverride.value);
    if (interval && interval.from && interval.to) {
        // override
        config.intervalName = interval.name;
        config.intervalFrom = interval.from;
        config.intervalTo = interval.to;
        if (interval.resolution) {
            config.resolution = interval.resolution;
        }
    } else {
        // reset to widget default
        config.intervalName = widget.value.intervalName;
        config.intervalFrom = widget.value.intervalFrom;
        config.intervalTo = widget.value.intervalTo;
        config.resolution = widget.value.resolution;
    }
    widgetOverride.value = config;
}
async function overrideDataSources(resources: WidgetConfigResource[]): Promise<void> {
    const config = Utils.deepCopy(widgetOverride.value);
    const resolvedResources = await Promise.all(resources.map(async (res) => {
        const generator = await Port.generators.getGeneratorByKey(res.resourceKey || '');
        res.resourceName = generator?.name || res.resourceName;
        return res;
    }));
    if (resolvedResources.length > 0) {
        // override
        const metrics = config.axis.flatMap((axis) => axis.metrics);
        metrics.forEach((metric) => {
            // anon resources are kept always
            const compareResources = metric.resources.filter((r) => r.resourceFilters !== undefined);
            if (resolvedResources.length === 1) {
                // time compares for non-anon resources shall be kept for the new resources
                // if we have more than x new resources, we omit the time compares, to make sure the total amount
                // of resources does not get to high
                const timeOverrides: (string|undefined)[] = metric.resources
                    .filter((r) => !r.resourceFilters)
                    .map((r) => r.timeOverride)
                    .filter(ArrayUtils.removeDuplicates);
                const res: WidgetConfigResource[] = resolvedResources
                    .flatMap((r) => timeOverrides
                        .map((t) => ({ ...r, timeOverride: t })));
                metric.resources = res.concat(compareResources);
            } else {
                metric.resources = Utils.deepCopy(resolvedResources).concat(compareResources);
            }
        });
        metrics.forEach((metric) => metric.resources = metric.resources.map((res) => ({
            ...res,
            seriesName: WidgetUtils.getDefaultSeriesName(config, metric, res),
        })));
    } else {
        // reset to widget default
        config.axis
            .forEach((axis, axisIndex) => axis.metrics
                .forEach((metric, metricIndex) => metric.resources = widget.value.axis[axisIndex].metrics[metricIndex].resources));
    }
    widgetOverride.value = config;
}
function updateWidgetActions() {
    menuActions.value = widgetLoader.value.getMenuActions();
}
function onLocaleChanged() {
    widgetOverride.value = Utils.deepCopy(widgetOverride.value);
}
function onRouteChanged() {
    showDropdown.value = false;
}
function updateWidgetSettings(settings: WidgetSettings) {
    widgetSettings.value = settings;
}
function applyUrlOverrides(): void {
    // from and to should be provided in format yyyy-mm-dd
    const intervalName = route.query.intervalname as string|undefined;
    const resolution = route.query.resolution as Resolution|undefined;
    let from = new Date(route.query.from as string|undefined || '');
    from = DateUtils.cropToPrecision(from, 'days');
    let to = new Date(route.query.to as string|undefined || '');
    to = DateUtils.cropToPrecision(to, 'days');

    if (intervalName && intervalName !== 'fixed') {
        overrideTimeInterval({
            name: intervalName,
            from: new Date(),
            to: new Date(),
            resolution: resolution,
        });
    } else if (from && to && to > from) {
        // only apply time range if it is valid
        overrideTimeInterval({
            name: 'fixed',
            from: from,
            to: to,
            resolution: resolution,
        });
    } else {
        overrideTimeInterval(undefined);
    }

    let generatorKeys: string[] = [];
    if (route.query.generator) {
        if (Array.isArray(route.query.generator)) {
            generatorKeys = route.query.generator as string[];
        } else {
            generatorKeys = [route.query.generator as string];
        }
    }
    overrideDataSources(generatorKeys
        .map((generatorKey) => ({
            uuid: generateUUID().substring(0, 8),
            type: ResourceType.Generator,
            resourceName: generatorKey,
            resourceKey: generatorKey,
            seriesName: generatorKey,
            aggregationOverGenerators: Aggregation.None,
            config: {},
        })));
}
function copy() {
    showDropdown.value = false;
    showInfo.value = false;
    if (store.getters['User/hasFeatureLicense'](LicenseFeature.ADVANCED)) {
        showCopyDlg.value = true;
    } else {
        showMissingLicenseDlg.value = true;
    }
}
function showCreateTemplateDialog() {
    showDropdown.value = false;
    showInfo.value = false;
    if (store.getters['User/hasFeatureLicense'](LicenseFeature.ADVANCED)) {
        showCreateTemplateDlg.value = true;
    } else {
        showMissingLicenseDlg.value = true;
    }
}
function edit() {
    openWizardInTab(undefined);
}
function changeTime() {
    openWizardInTab(WidgetWizardTab.Time);
}
function openWizardInTab(tab?: WidgetWizardTab, action?: string) {
    showDropdown.value = false;
    wizardTab.value = tab || null;
    wizardTabAction.value = action || null;
    showWizardDlg.value = true;
}
function handleError(action: string) {
    if (action === 'license') {
        showMissingLicenseDlg.value = true;
    } else {
        openWizardInTab(action as WidgetWizardTab);
    }
}
async function exportConfig(): Promise<void> {
    showDropdown.value = false;
    const filename = `${title.value}-${generateUUID().substring(0, 8)}.json`;
    FileUtils.downloadTextAsFile(filename, JSON.stringify(widget.value, null, 4));
}
function performAction(callback: () => void) {
    showDropdown.value = false;
    showInfo.value = false;
    callback();
    updateWidgetActions();
}
/**
 * Helper function that toggles fullscreen. Also removes chart from dom through chart-loading property, to avoid
 * laggy chart resize.
 */
function toggleFullscreen() {
    showDropdown.value = false;
    nextTick(() => {
        expand.value = !expand.value;
        if (expand.value) {
            Utils.disableScrolling();
            if (widgetTitle.value) {
                widgetTitle.value.focus();
            }
        } else {
            Utils.enableScrolling();
        }
    });
}
async function addFavorit() {
    showDropdown.value = false;
    await Port.widgets.addWidgetToFavorites(widget.value);
    isFavorite.value = true;
}
async function removeFavorit() {
    showDropdown.value = false;
    await Port.widgets.removeWidgetFromFavorites(widget.value);
    isFavorite.value = false;
}
async function updateWidget(widgetConfig: WidgetConfig) {
    await Port.widgets.updateWidget(widgetConfig);
    widget.value = widgetConfig;
    widgetOverride.value = widget.value;
    showWizardDlg.value = false;
}
async function deleteWidget(): Promise<void> {
    showDropdown.value = false;
    showInfo.value = false;
    if (!await Port.user.getUserHasLicenseFeature(LicenseFeature.ADVANCED)) {
        showMissingLicenseDlg.value = true;
    } else {
        showDeleteConfirmDlg.value = true;
    }
    updateWidgetActions();
}
async function confirmDeleteWidget() {
    await Port.widgets.deleteWidget(widget.value);
    showDeleteConfirmDlg.value = false;
}

function openRetryStreamingDlg() {
    showRetryStreamingDlg.value = true;
}

function retryStreaming() {
    widgetLoader.value.getWidget().loadWidget();
    showRetryStreamingDlg.value = false;
}

watch(() => props.config, onWidgetConfigChanged);
watch(() => showDropdown.value, updateWidgetActions);
watch(() => i18n.locale, onLocaleChanged);
watch(() => route, onRouteChanged);
watch(() => route.query, applyUrlOverrides);

onMounted(async () => {
    await onWidgetConfigChanged();
    await fetchPortfolio();
    await fetchDashboard();
    applyUrlOverrides();
});

return { __sfc: true,props, route, widgetLoader, widgetTitle, widgetInfo, menuActions, widget, widgetOverride, wizardTab, wizardTabAction, dataIntegrity, widgetSettings, portfolio, dashboard, showMissingDataDlg, showCopyDlg, showMissingLicenseDlg, showDeleteConfirmDlg, showCreateTemplateDlg, showRetryStreamingDlg, showWizardDlg, showDropdown, showInfo, expand, isFavorite, portfolioName, dashboardName, title, useHeaderBackground, fetchPortfolio, fetchDashboard, onWidgetConfigChanged, overrideTimeInterval, overrideDataSources, updateWidgetActions, onLocaleChanged, onRouteChanged, updateWidgetSettings, applyUrlOverrides, copy, showCreateTemplateDialog, edit, changeTime, openWizardInTab, handleError, exportConfig, performAction, toggleFullscreen, addFavorit, removeFavorit, updateWidget, deleteWidget, confirmDeleteWidget, openRetryStreamingDlg, retryStreaming, BubbleMenu, WidgetTimeChooser, ConfirmDlg, WidgetWizard, DashboardWidgetInfo, CopyWidgetDlg, InfoDlg, Role, MissingLicenseDlg, WidgetLoader, NoteBox, RoleGuard, CreateWidgetTemplateDlg, WidgetStreaming, WidgetStreamingFailed }
}

})